import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

function HelpContext() {
  const navigate = useNavigate();
  const [helpContexts, setHelpContexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contextToDelete, setContextToDelete] = useState(null);

  const fetchHelpContexts = async () => {
    try {
      setLoading(true);
      const helpContextsRef = collection(db, 'help-context');
      const querySnapshot = await getDocs(helpContextsRef);
      const helpContextsData = [];
      querySnapshot.forEach((doc) => {
        helpContextsData.push({ id: doc.id, ...doc.data() });
      });
      // Sort by contextId
      helpContextsData.sort((a, b) => {
        if (!a.contextId) return 1;
        if (!b.contextId) return -1;
        return a.contextId.localeCompare(b.contextId);
      });
      setHelpContexts(helpContextsData);
      setError(null);
    } catch (err) {
      console.error("Error fetching help contexts:", err);
      setError("Failed to load help contexts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHelpContexts();
  }, []);

  const handleEdit = (contextId) => {
    navigate(`/help-context/${contextId}`);
  };

  const handleDelete = async () => {
    if (!contextToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'helpContext', contextToDelete.id));
      setHelpContexts(helpContexts.filter(c => c.id !== contextToDelete.id));
      setDeleteConfirmOpen(false);
      setContextToDelete(null);
    } catch (err) {
      console.error("Error deleting help context:", err);
      setError("Failed to delete help context");
    }
  };

  const handleDeleteClick = (context) => {
    setContextToDelete(context);
    setDeleteConfirmOpen(true);
  };

  const truncateData = (data, maxLength = 100) => {
    if (!data) return 'N/A';
    if (data.length <= maxLength) return data;
    return data.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Help Context</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/help-context/new')}
        >
          New Help Context
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Context ID</TableCell>
              <TableCell>Data Preview</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {helpContexts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No help contexts found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              helpContexts.map((context) => (
                <TableRow key={context.id} hover>
                  <TableCell>{context.contextId || 'N/A'}</TableCell>
                  <TableCell>{truncateData(context.data)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(context.id)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(context)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the help context "{contextToDelete?.contextId}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HelpContext;
